/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import setPageMetadata from "@insite/client-framework/Common/Utilities/setPageMetadata";
import { trackPageChange } from "@insite/client-framework/Common/Utilities/tracking";
import { HasShellContext } from "@insite/client-framework/Components/IsInShell";
import { redirectTo, setStatusCode } from "@insite/client-framework/ServerSideRendering";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setBreadcrumbs from "@insite/client-framework/Store/Components/Breadcrumbs/Handlers/SetBreadcrumbs";
import {
    getSearchDataModeActive,
    getSelectedCategoryPath,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import clearAllProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/ClearAllProductFilters";
import clearProducts from "@insite/client-framework/Store/Pages/ProductList/Handlers/ClearProducts";
import displayProducts from "@insite/client-framework/Store/Pages/ProductList/Handlers/DisplayProducts";
import translate from "@insite/client-framework/Translate";
import PageProps from "@insite/client-framework/Types/PageProps";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import { getPrevCurrentNextQueryString } from "@insite/mobius/utilities/linkElementsManipulation";
import handleBlurToScrollableAccordion from "@monsma/XDL/Utilities/handleBlurToScrollableAccordion";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const selectedCategoryPath = getSelectedCategoryPath(state);
    const path =
        selectedCategoryPath || (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
    const {
        pages: {
            productList: { isLoading, productFilters, parameter, isSearchPage, filterQuery, productInfosByProductId },
        },
    } = state;
    const catalogPage = getCatalogPageStateByPath(state, path).value;
    const productsDataView = getProductsDataView(state, parameter);
    return {
        isLoading,
        stockedItemsOnly: productFilters.stockedItemsOnly,
        previouslyPurchasedProducts: productFilters.previouslyPurchasedProducts,
        query: productFilters.query,
        productsDataView,
        firstProductDetailPath: productsDataView.value
            ? productInfosByProductId[productsDataView.value[0]?.id]?.productDetailPath
            : undefined,
        filterQuery,
        isSearchPage,
        search: location.search,
        path,
        breadcrumbLinks: state.components.breadcrumbs.links,
        productListCatalogPage: getCatalogPageStateByPath(state, path).value,
        websiteName: state.context.website.name,
        pages: state.pages,
        location: getLocation(state),
        category: getCategoryState(state, catalogPage?.categoryIdWithBrandId ?? catalogPage?.categoryId).value,
        websiteSettings: getSettingsCollection(state).websiteSettings,
        searchDataModeActive: getSearchDataModeActive(state),
    };
};

const mapDispatchToProps = {
    clearAllProductFilters,
    displayProducts,
    setBreadcrumbs,
    clearProducts,
};

type Props = HasHistory &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasShellContext &
    PageProps;

const withProducts = (Component: React.ComponentType<any>) => {
    class LoadAndDisplayProducts extends React.Component<any> {
        loadProducts() {
            const { displayProducts, path, search } = this.props;

            displayProducts({
                path,
                queryString: search,
                isSearch: this.isSearchPath(),
                restrictAttributesToCategory: false, // XNG-896: add restrictAttributesToCategory to product api for L2 categories
            });
        }

        isSearchPath() {
            return !!this.props.path.match(/^\/search\b/i);
        }

        getQueryStrings() {
            if (this.props.productsDataView.value && this.props.productsDataView.pagination) {
                const { prevPageUri, nextPageUri, currentPage } = this.props.productsDataView.pagination;
                const { prevQueryString, currentQueryString, nextQueryString } = getPrevCurrentNextQueryString(
                    prevPageUri,
                    nextPageUri,
                    currentPage,
                    this.props.filterQuery ?? "",
                );
                return { prevQueryString, currentQueryString, nextQueryString };
            }

            const { prevQueryString, currentQueryString, nextQueryString } = getPrevCurrentNextQueryString("", "", 1, "");
            return { prevQueryString, currentQueryString, nextQueryString };
        }

        setMetadata() {
            const { productListCatalogPage, websiteName, location, websiteSettings } = this.props;

            if (!productListCatalogPage) {
                return;
            }

            const {
                metaDescription,
                metaKeywords,
                openGraphImage,
                openGraphTitle,
                openGraphUrl,
                title,
                primaryImagePath,
                canonicalPath,
            } = productListCatalogPage;

            const { prevQueryString, currentQueryString, nextQueryString } = this.getQueryStrings();

            setPageMetadata(
                {
                    metaDescription,
                    metaKeywords,
                    openGraphImage: openGraphImage || primaryImagePath,
                    openGraphTitle,
                    openGraphUrl,
                    title,
                    currentPath: location.pathname,
                    canonicalPath,
                    websiteName,
                    currentQueryString,
                    prevQueryString,
                    nextQueryString,
                },
                websiteSettings,
            );
        }
        
        setSearchPageMetadata(title: string) {
            const { websiteName, location, websiteSettings, fields, productsDataView, alternateLanguageUrls } = this.props;
            const firstProduct = productsDataView.value && productsDataView.value[0];
            const { prevQueryString, currentQueryString, nextQueryString } = this.getQueryStrings();

            setPageMetadata(
                {
                    title,
                    currentPath: location.pathname,
                    websiteName,
                    openGraphImage:
                        fields.openGraphImage ||
                        firstProduct?.largeImagePath ||
                        firstProduct?.mediumImagePath ||
                        firstProduct?.smallImagePath,
                    openGraphTitle: fields.openGraphTitle,
                    openGraphUrl: location.pathname + location.search,
                    alternateLanguageUrls,
                    currentQueryString,
                    prevQueryString,
                    nextQueryString,
                },
                websiteSettings,
            );
        }

        setProductListBreadcrumbs() {
            this.props.setBreadcrumbs({
                links: this.props.productListCatalogPage!.breadCrumbs!.map((o: any) => ({
                    children: o.text,
                    href: o.url,
                })),
            });
        }

        setSearchBreadcrumbs() {
            this.props.setBreadcrumbs({
                links: [
                    { children: translate("Home"), href: "/" },
                    { children: `${translate("Search Results")}: ${this.props.query}` },
                ],
            });
        }

        UNSAFE_componentWillMount(): void {
            const { productsDataView } = this.props;

            if (!productsDataView.value && !productsDataView.isLoading) {
                this.loadProducts();
            } else if (!IS_SERVER_SIDE && productsDataView.value) {
                const { searchTermRedirectUrl } = productsDataView;

                // Check that the request is not a redirection to where we are currently
                if (searchTermRedirectUrl && window?.location?.toString() !== searchTermRedirectUrl) {
                    if (searchTermRedirectUrl.lastIndexOf("http", 0) === 0) {
                        window.location.replace(searchTermRedirectUrl);
                    } else {
                        this.props.history.replace(searchTermRedirectUrl);
                    }
                    return;
                }
            } else if (this.props.isSearchPage) {
                this.redirectToProductDetailIfExactMatch();
            }

            if (this.props.productListCatalogPage) {
                this.setMetadata();

                if (
                    this.props.breadcrumbLinks?.map((o: any) => o.children?.toString()).join(",") !==
                    this.props.productListCatalogPage.breadCrumbs?.map((o: any) => o.text).join(",")
                ) {
                    this.setProductListBreadcrumbs();
                }
            } else if (this.props.isSearchPage) {
                this.setSearchPageMetadata(translate("Search Results"));

                if (!this.props.breadcrumbLinks) {
                    this.setSearchBreadcrumbs();
                }
            }
        }

        componentWillUnmount(): void {
            this.props.clearProducts();

            handleBlurToScrollableAccordion("remove");
        }

        componentDidUpdate(prevProps: Props): void {
            const {
                isLoading,
                filterQuery,
                location: { pathname, search },
                firstProductDetailPath,
                productListCatalogPage,
                searchDataModeActive,
                productsDataView,
            } = this.props;

            (async () => {
                if (!isLoading && !productsDataView.value && !productsDataView.isLoading) {
                    await this.loadProducts();
                }
            })();

            // handle the query string change requests initiated by the filtering widget setQueryFilter calls
            // Make sure we are fully loaded, since a loading product list will change the filteQuery to match the current pathname/search
            // This can trigger a loop with the pathname/search updating and the filterQuery not updated till the productList is loaded
            if (!isLoading && filterQuery !== prevProps.filterQuery) {
                const currentUrl = `${pathname}${search}`;
                const newUrl = filterQuery ? `${pathname}?${filterQuery}` : pathname;
                if (newUrl !== currentUrl) {
                    this.props.history.push(newUrl);
                }
                return;
            }

            if (!isLoading && productsDataView.value && pathname === prevProps.location.pathname) {
                // Check that the request is not a redirection to where we are currently
                const { searchTermRedirectUrl } = productsDataView;
                if (searchTermRedirectUrl && window?.location?.toString() !== searchTermRedirectUrl) {
                    if (searchTermRedirectUrl.lastIndexOf("http", 0) === 0) {
                        window.location.replace(searchTermRedirectUrl);
                    } else {
                        this.props.history.replace(searchTermRedirectUrl);
                    }
                    return;
                }

                this.redirectToProductDetailIfExactMatch();
            }

            if (!isLoading && (search !== prevProps.search || this.props.path !== prevProps.path)) {
                if (this.props.path !== prevProps.path) {
                    this.props.clearAllProductFilters({
                        removeAll: true,
                    });
                }
                this.loadProducts();
            }

            if (productListCatalogPage) {
                this.setMetadata();
                trackPageChange();
                if (
                    productListCatalogPage.categoryId !== prevProps.productListCatalogPage?.categoryId ||
                    !this.props.breadcrumbLinks
                ) {
                    this.setProductListBreadcrumbs();
                }
            } else if (this.props.isSearchPage) {
                // For pages that do not have a productListCatalogPage, mainly the Search Page
                this.setSearchPageMetadata(translate("Search Results"));
                trackPageChange();
                const prevQuery = this.props.breadcrumbLinks
                    ? this.props.breadcrumbLinks[this.props.breadcrumbLinks.length - 1].children
                    : "";
                const queryChanged = prevQuery !== `${translate("Search Results")}: ${this.props.query}`;
                if (queryChanged || !this.props.breadcrumbLinks) {
                    this.setSearchBreadcrumbs();
                }
            }

            if (searchDataModeActive !== prevProps.searchDataModeActive) {
                // refresh the search results when search data mode has been toggled in the cms shell
                this.loadProducts();
            }

            handleBlurToScrollableAccordion();
        }

        componentDidMount() {
            handleBlurToScrollableAccordion();
        }

        redirectToProductDetailIfExactMatch() {
            const {
                location: { search },
                firstProductDetailPath,
                productsDataView,
            } = this.props;

            if (!productsDataView.value) {
                return;
            }

            const { exactMatch, value: products } = productsDataView;

            if (exactMatch && !this.props.stockedItemsOnly) {
                let productDetailUrl = firstProductDetailPath ?? products[0].canonicalUrl;

                const parsedQuery = parseQueryString<{ query: string }>(search);
                const query = parsedQuery.query;

                if (!query) {
                    return;
                }

                if (productDetailUrl.indexOf("?") !== -1) {
                    productDetailUrl += `&criteria=${encodeURIComponent(query)}`;
                } else {
                    productDetailUrl += `?criteria=${encodeURIComponent(query)}`;
                }

                if (IS_SERVER_SIDE) {
                    setStatusCode(302);
                    redirectTo(productDetailUrl);
                } else {
                    this.props.history.replace(productDetailUrl);
                }
            }
        }

        render() {
            if (
                this.props.productsDataView.value &&
                this.props.productsDataView.exactMatch &&
                !this.props.stockedItemsOnly
            ) {
                // prevent a flash of data when PLP immediately redirects to PDP
                return null;
            }

            return <Component {...this.props} />;
        }
    }

    return connect(mapStateToProps, mapDispatchToProps)(withHistory(LoadAndDisplayProducts));
};

export default withProducts;
